import Loader from "@/components/atoms/Loader";
import { Modal } from "@/components/molecules/Modal";
import { OtpInputComponent } from "@/components/molecules/OtpInputComponent";
import ToastAlert from "@/components/molecules/ToastAlert";
import { Navbar } from "@/components/organisms/Navbar";
import SignupPhoneInput from "@/components/organisms/SignUpPhoneInput";
import {
  AUTH_EVENTS
} from "@/constants/amplitude.constants";
import { DEFAULT_COUNTRY_CODE } from "@/shared/constants/common.constants";
import { RECAPTCHA_TOKEN } from "@/shared/constants/env/externalService.constants";
import { ERRORS_COPIES } from "@/shared/constants/errors.constants";
import { SIGNUP_TYPES } from "@/shared/constants/signUpConstants";
import SignupController from "@/shared/controllers/SignUp";
import { TOAST_TYPES } from "@/shared/data/enums/toast";
import { NavbarDataType, navbarData_en, navbarData_us } from "@/shared/data/navbar.data";
import { navbarData_kr } from "@/shared/data/navbar.data.kr";
import Amplitude from "@/shared/services/amplitude.service";
import { useDataStore } from "@/shared/store/useDataStore";
import useModalStore from "@/shared/store/useModalStore";
import useSignUpStore from "@/shared/store/useSignUpStore";
import { SIGNUP_TYPE } from "@/shared/types/enums/signup";
import { SIGNUP_FORM_FIELDS } from "@/shared/types/signup.types";
import {
  getCurrentURL,
  getReferrer,
  getScreenWidth,
} from "@/shared/utils/commons";
import { isBrowser } from "@/shared/utils/dom";
import { emptyObject } from "@/shared/utils/empty";
import { onFormInputChange } from "@/shared/utils/signup";
import { spayeeAuthenticateSignUp } from "@/shared/utils/spayee.utils";
import { clearFromLocal } from "@/shared/utils/storage.util";
import { getQueryObjFromURL, isRunningOnRootHost } from "@/shared/utils/url.util";
import { getUTMFieldsFromLocalStorage } from "@/shared/utils/utm.util";
import { Button, Div, H3, INPUT_TYPES, Input } from "@graphy/ui";
import * as EmailValidator from "email-validator";
import generator from "generate-password";
import { GetStaticProps, InferGetServerSidePropsType } from "next";
import Image from "next/image";
import { useRouter } from "next/router";
import { parseCookies } from "nookies";
import { useCallback, useEffect, useRef, useState } from "react";
import { getServerSideProps } from "..";
import BannerImg from "../../assets/signup/signupBanner.svg";

export const getStaticProps: GetStaticProps<{
  navbarData: NavbarDataType;
}> = async (context) => {
  const navbarData = {
    en: navbarData_en,
    us: navbarData_us,
    kr: navbarData_kr,
    in: navbarData_en,
  }[(context.locale as "en" | "kr" | "us" | "in") ?? "en"];

  return {
    props: {
      navbarData,
    },
  };
};

const SignUp = ({
  navbarData,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { openAuthModal, openOTPModal, setOpenOTPModal } = useModalStore();
  const [fpromTrack, setFpromTrack] = useState(null);
  const [fpromCode, setFpromCode] = useState(null);
  const [sentOTP, setSentOTP] = useState(false);
  const [domain, setDomain] = useState<string>("");
  const [resendOtp, setResendOtp] = useState(60);
  const router = useRouter();

  
  const { signupCopies } = navbarData;

  const queryParams = getQueryObjFromURL();
  const appsumoCode = queryParams.code

  useEffect(() => {
    window.addEventListener("message", (event) => {
      if (
        event.origin.includes("graphy") ||
        event.origin === "http://localhost:3000"
      ) {
        setFpromTrack(event.data._fprom_track);
        setFpromCode(event.data._fprom_code);
      }
    });
  }, []);

  const cookies = parseCookies();

  const { countryCode } = useDataStore();
  const cookieCountryCode = cookies?.countryCode || DEFAULT_COUNTRY_CODE;
  const finalCountryCode = countryCode || cookieCountryCode;

  let intervalRef = useRef<ReturnType<typeof setInterval>>();

  const {
    signUpData,
    setSignUpData,
    contactNumber,
    signUpInputsErrors,
    setSignUpInputsErrors,
    callingCode,
    isFormLoading,
    setIsFormLoading,
    parentOrigin,
    OTPType,
    setOTPType,
  } = useSignUpStore();

  const onEmailSignUpInputChange = useCallback(
    (value: string, key: string) => {
      onFormInputChange(key);

      setSignUpData({
        ...signUpData,
        [key]: value,
      });
    },
    [setSignUpData, signUpData]
  );

  const clearFromLocalStorage = useCallback(() => {
    if (isRunningOnRootHost()) {
      clearFromLocal("utmFields");
      clearFromLocal("gref");
      clearFromLocal("QUERY_PARAMS");
    }
  }, []);

  const actionAmplitudeEvents = useCallback(
    (key: string) => {
      const {
        current_url,
        screen_width,
        referrer,
        utm_params,
        signup_type,
        email,
      } = AUTH_EVENTS.SIGNUP_EMAIl_ENTERED.PROPERTIES;
      const { name } = AUTH_EVENTS.SIGNUP_NAME_ENTERED.PROPERTIES;
      if (key === SIGNUP_FORM_FIELDS.EMAIL) {
        new Amplitude().sendEvent({
          type: AUTH_EVENTS.SIGNUP_EMAIl_ENTERED.LABEL,
          properties: {
            [current_url]: getCurrentURL(),
            [screen_width]: getScreenWidth(),
            [referrer]: getReferrer(),
            [utm_params]: getUTMFieldsFromLocalStorage(),
            [signup_type]: SIGNUP_TYPES.FULLPAGE,
            [email]: signUpData?.email,
          } as { [key: string]: string },
        });
      }

      if (key === SIGNUP_FORM_FIELDS.SCHOOL_NAME) {
        new Amplitude().sendEvent({
          type: AUTH_EVENTS.SIGNUP_NAME_ENTERED.LABEL,
          properties: {
            [current_url]: getCurrentURL(),
            [screen_width]: getScreenWidth(),
            [referrer]: getReferrer(),
            [utm_params]: getUTMFieldsFromLocalStorage(),
            [signup_type]: SIGNUP_TYPES.FULLPAGE,
            [name]: signUpData?.schoolName,
          } as { [key: string]: string },
        });
      }
    },
    [signUpData?.email, signUpData?.schoolName]
  );

  const onEmailSignUpInputBlur = useCallback(
    (value: string, key: string, shouldReturnMessage = false) => {
      actionAmplitudeEvents(key);
      let errorMessage: string = "";

      const inputVal = (value || "").trim();

      if (inputVal !== "") {
        if (key === SIGNUP_FORM_FIELDS.EMAIL) {
          if (!EmailValidator.validate(inputVal)) {
            errorMessage = ERRORS_COPIES.INVALID_EMAIL;
          }
        }
      } else {
        if (key === SIGNUP_FORM_FIELDS.EMAIL) {
          errorMessage = ERRORS_COPIES.INVALID_EMAIL;
        } else if (key === SIGNUP_FORM_FIELDS.SCHOOL_NAME) {
          errorMessage = ERRORS_COPIES.REQUIRED_NAME;
        } else if (key === SIGNUP_FORM_FIELDS.PHONE) {
          // Phone is optional, no error message needed
        } else errorMessage = ERRORS_COPIES.REQUIRED_INPUT;
      }

      if (errorMessage && !shouldReturnMessage) {
        setSignUpInputsErrors({
          ...signUpInputsErrors,
          [key]: errorMessage,
        });
      }
      if (shouldReturnMessage) {
        return errorMessage;
      }
    },
    [actionAmplitudeEvents, setSignUpInputsErrors, signUpInputsErrors]
  );

  const actionSignUpSuccessAmplitudeEvent = useCallback(
    (userId: string) => {
      const {
        current_url,
        screen_width,
        referrer,
        utm_params,
        name,
        email,
        phone_number,
        phone_country,
        signup_type,
      } = AUTH_EVENTS.CREATOR_SIGNUP_SUCCESS.PROPERTIES;
      const amplitudeProperties = {
        [current_url]: getCurrentURL(),
        [screen_width]: getScreenWidth(),
        [referrer]: getReferrer(),
        [utm_params]: getUTMFieldsFromLocalStorage(),
        [name]: signUpData?.schoolName,
        [email]: signUpData?.email,
        [phone_number]: contactNumber || "", // Ensure empty string is sent when no phone number
        [phone_country]: callingCode,
        [signup_type]: SIGNUP_TYPES.FULLPAGE,
      };
      new Amplitude().sendEvent({
        type: AUTH_EVENTS.CREATOR_SIGNUP_SUCCESS.LABEL,
        properties: amplitudeProperties as { [key: string]: string },
        userId: userId,
      });
    },
    [callingCode, contactNumber, signUpData?.email, signUpData?.schoolName]
  );

  const actionSignUpSuccessServerAmplitudeEvent = useCallback(() => {
    const {
      referrer,
      utm_params,
      name,
      email,
      phone_number,
      phone_country,
      signup_type,
    } = AUTH_EVENTS.CRETOR_SIGNUP_SUCCESS_SERVER.PROPERTIES;
    const amplitudeProperties = {
      [referrer]: getReferrer(),
      [utm_params]: getUTMFieldsFromLocalStorage(),
      [name]: signUpData?.schoolName,
      [email]: signUpData?.email,
      [phone_number]: contactNumber || "", // Ensure empty string is sent when no phone number
      [phone_country]: callingCode,
      [signup_type]: SIGNUP_TYPES.FULLPAGE,
    };
    new Amplitude().sendEvent({
      type: AUTH_EVENTS.CRETOR_SIGNUP_SUCCESS_SERVER.LABEL,
      properties: amplitudeProperties as { [key: string]: string },
    });
  }, [callingCode, contactNumber, signUpData?.email, signUpData?.schoolName]);

  const handleOTPSignup = useCallback(
    async (otpType: string) => {
      let token = "";

      const password = generator.generate({
        length: 10,
        numbers: true,
      });
      //@ts-ignore
      if (typeof grecaptcha !== "undefined") {
        //@ts-ignore
        token = await grecaptcha?.execute(RECAPTCHA_TOKEN, {
          action: "submit",
        });
      }

      const { email, schoolName } = signUpData || emptyObject;
      if (EmailValidator.validate(email) && !!schoolName) {
        const OTPVerificationValues = {
          email,
          phone: contactNumber ? encodeURIComponent(`+${callingCode}${contactNumber}`) : "",
          type: otpType,
          token,
          name: schoolName,
          source: "appsumo"
        };

        const otpVerified = await SignupController.verifyOTP(
          OTPVerificationValues
        );

        if (otpVerified.response) {
          setOpenOTPModal(true);
          setOTPType(otpType);
          setSentOTP(true);
          handleOTPResend();
        }

        if (!otpVerified.response && otpVerified?.message) {
          ToastAlert({
            toastType: TOAST_TYPES.ERROR,
            message: otpVerified?.message,
          });
        }
      } else {
        let inputErrors = {
          email:
            onEmailSignUpInputBlur(email, SIGNUP_FORM_FIELDS.EMAIL, true) || "",
          phone:
            onEmailSignUpInputBlur(
              contactNumber,
              SIGNUP_FORM_FIELDS.PHONE,
              true
            ) || "",
          schoolName:
            onEmailSignUpInputBlur(
              schoolName,
              SIGNUP_FORM_FIELDS.SCHOOL_NAME,
              true
            ) || "",
        };

        setSignUpInputsErrors(inputErrors);
      }
    },
    [
      callingCode,
      contactNumber,
      onEmailSignUpInputBlur,
      parentOrigin,
      setSignUpInputsErrors,
      signUpData,
    ]
  );

  const decreaseTimer = () => {
    setResendOtp(function (prevCount) {
      if (prevCount > 0) {
        return (prevCount -= 1);
      } else {
        return (prevCount = 0);
      }
    });
  };

  const handleOTPResend = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(decreaseTimer, 1000);
  };

  const actionResendOTPOnSignUp = () => {
    setResendOtp(60);
    handleOTPResend();
    handleOTPSignup(OTPType);
  };

  const closeModal = () => {
    setOpenOTPModal(false);
    setSentOTP(false);
    setOTPType("");
    setResendOtp(60);
  };

  const renderOTPOnSignUp = useCallback(() => {
    return (
      <Modal showModal={openOTPModal} onClose={closeModal}>
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3">
            <span className="">
              {`OTP sent to ${
                OTPType === "phone"
                  ? `+${callingCode}${contactNumber}`
                  : signUpData?.email
              }`}{" "}
            </span>

            {sentOTP && (
              <>
                {resendOtp > 0 ? (
                  <span className="text-center text-[14px] leading-[20px] text-grey-3 md:hidden">{`${signupCopies.ctas.RESEND_IN} ${resendOtp}s`}</span>
                ) : (
                  <span
                    className="flex cursor-pointer items-center justify-center text-center text-[14px] font-semibold leading-[20px] text-sapphire02 md:hidden"
                    onClick={actionResendOTPOnSignUp}
                  >
                    {signupCopies.ctas.RESEND}
                  </span>
                )}
              </>
            )}

            {!sentOTP ? (
              <>Sending...</>
            ) : (
              <>
                {resendOtp > 0 ? (
                  <span className="hidden text-center text-[14px] leading-[20px] text-grey-3 md:block">{`${signupCopies.ctas.RESEND_IN} ${resendOtp}s`}</span>
                ) : (
                  <span
                    className="hidden cursor-pointer items-center justify-center text-center text-[14px] font-semibold leading-[20px] text-sapphire02 md:flex"
                    onClick={actionResendOTPOnSignUp}
                  >
                    {signupCopies.ctas.RESEND}
                  </span>
                )}
              </>
            )}
          </div>
          {sentOTP && (
            <OtpInputComponent
              creatorDomain={domain}
              signupCopies={signupCopies}
              OTPActionType={"signup"}
            />
          )}
        </div>
        <form
          method="post"
          target="_parent"
          id="spayee-signup-form"
          className="hidden"
        >
          <input id="spayee-signup-form-email" name="email" />
          <input id="spayee-signup-form-password" name="password" />
          <input id="spayee-signup-form-age" name="age" value="" readOnly />
        </form>
      </Modal>
    );
  }, [OTPType, sentOTP, resendOtp]);

  const handleEmailSignup = useCallback(async () => {
    let token = "";

    const password = generator.generate({
      length: 10,
      numbers: true,
    });
    //@ts-ignore
    if (typeof grecaptcha !== "undefined") {
      //@ts-ignore
      token = await grecaptcha?.execute(RECAPTCHA_TOKEN, {
        action: "submit",
      });
    }

    const { email, schoolName } = signUpData || emptyObject;
    if (EmailValidator.validate(email) && !!schoolName) {
      const signupValues = {
        email,
        phone: contactNumber ? `+${callingCode}${contactNumber}` : "",
        countryCode: callingCode,
        name: schoolName,
        recaptchaToken: token,
        signupType: SIGNUP_TYPE.NORMAL,
        password: password,
        _fprom_code: fpromCode,
        _fprom_track: fpromTrack,
        appsumoCode,
      };

      // @ts-ignore
      const emailResponse = await SignupController.emailSignup(signupValues);

      if (emailResponse.response && !emailResponse.old_graphy_user) {
        clearFromLocalStorage();
        const { domain } = emailResponse;
        //@ts-ignore
        actionSignUpSuccessAmplitudeEvent(emailResponse.userId);
        spayeeAuthenticateSignUp({
          domain,
          email,
          password,
        });
      }
      if (emailResponse.response && emailResponse.domain) {
        actionSignUpSuccessServerAmplitudeEvent();
      }
      if (!emailResponse.response && emailResponse.message) {
        ToastAlert({
          toastType: TOAST_TYPES.ERROR,
          message: emailResponse?.message,
        });
      }
      if (emailResponse.old_graphy_user) {
        if (parentOrigin) {
          window.location.href = `${parentOrigin}/?signin&email=${email}`;
        } else {
          window.location.href = `/?signin&email=${email}`;
        }
      }
    } else {
      let inputErrors = {
        email:
          onEmailSignUpInputBlur(email, SIGNUP_FORM_FIELDS.EMAIL, true) || "",
        phone:
          onEmailSignUpInputBlur(
            contactNumber,
            SIGNUP_FORM_FIELDS.PHONE,
            true
          ) || "",
        schoolName:
          onEmailSignUpInputBlur(
            schoolName,
            SIGNUP_FORM_FIELDS.SCHOOL_NAME,
            true
          ) || "",
      };

      setSignUpInputsErrors(inputErrors);
    }
  }, [
    actionSignUpSuccessAmplitudeEvent,
    actionSignUpSuccessServerAmplitudeEvent,
    callingCode,
    contactNumber,
    fpromCode,
    fpromTrack,
    onEmailSignUpInputBlur,
    parentOrigin,
    setSignUpInputsErrors,
    signUpData,
  ]);

  const handleOTPVerification = () => {
    let otpType = "email";
    handleOTPSignup(otpType);
  };

  const renderEmailSignUpForm = useCallback(() => {
    return (
      <div className="flex flex-col gap-[16px] md:gap-x-6">
        <Input
          containerMargin=""
          className=" mb-2  h-[44px] w-full rounded-[8px] border border-grey-3 p-[16px]"
          type={INPUT_TYPES.TEXT}
          value={signUpData?.schoolName}
          inputLimit={200}
          errorMessage={signUpInputsErrors?.[SIGNUP_FORM_FIELDS.SCHOOL_NAME]}
          onChange={(value) => {
            onEmailSignUpInputChange(
              value as string,
              SIGNUP_FORM_FIELDS.SCHOOL_NAME
            );
          }}
          onBlur={(value) => {
            onEmailSignUpInputBlur(value, SIGNUP_FORM_FIELDS.SCHOOL_NAME);
          }}
          placeholder={signupCopies.name.placeholder}
        />
        <Input
          containerMargin=""
          className=" mb-2 mt-1 h-[44px] w-full rounded-[8px] border border-grey-3 p-[16px]"
          type={INPUT_TYPES.EMAIL}
          inputLimit={200}
          errorMessage={signUpInputsErrors?.[SIGNUP_FORM_FIELDS.EMAIL]}
          onChange={(value) =>
            onEmailSignUpInputChange(value as string, SIGNUP_FORM_FIELDS.EMAIL)
          }
          value={signUpData?.email}
          onBlur={(value) =>
            onEmailSignUpInputBlur(value, SIGNUP_FORM_FIELDS.EMAIL)
          }
          placeholder={signupCopies.email.placeholder}
        />
        <SignupPhoneInput signupCopies={signupCopies} />
        <Button
          className="mb-[24px] mt-2  flex  h-[40px] items-center justify-center rounded-[8px] !bg-sapphire md:mb-[42px] "
          onClick={handleOTPVerification}
          ripple={false}
          clickId="signup-button"
        >
          <Div className="text-center font-normal text-white">
            {isFormLoading ? <Loader /> : signupCopies.ctas.START_NOW}
          </Div>
        </Button>
        <form
          method="post"
          target="_parent"
          id="spayee-signup-form"
          className="hidden"
        >
          <input id="spayee-signup-form-email" name="email" />
          <input id="spayee-signup-form-password" name="password" />
          <input id="spayee-signup-form-age" name="age" value="" readOnly />
        </form>
      </div>
    );
  }, [
    signUpData?.schoolName,
    signUpData?.email,
    signUpInputsErrors,
    handleOTPSignup,
    isFormLoading,
    onEmailSignUpInputChange,
    onEmailSignUpInputBlur,
    OTPType,
    resendOtp,
  ]);

  if(!navbarData){
    return <div>Error loading data</div>;
  }

  return (
    <>
      <Navbar
        navbarData={{ navbarItems: [], signupCopies: navbarData.signupCopies }}
      />
      <div
        className="graphy-container mx-auto flex flex-col tab:flex-row tab:gap-[95px] tab:py-[200px] web:flex-row  web:gap-[174px] 
      "
      >
        <div className="w-full">
          <H3 className="mb-[4px] mt-6 w-full text-left text-title-20 font-semibold sm:mt-0 md:mb-[6px]  tab:text-title-24  web:text-title-24 ">
            {signupCopies.appsumoSignupHeading}
          </H3>
          <p className="mb-[24px] text-[14px] text-grey-3 web:mb-[48px]">
            {signupCopies.signupDescription}
          </p>
          {renderEmailSignUpForm()}
        </div>
        <div className=" w-full">
          <Image src={BannerImg} className="w-full" alt="signup-heroImage" />
        </div>
        {openOTPModal && renderOTPOnSignUp()}
      </div>
    </>
  );
};
export default SignUp;
